import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { getPersonWiseReport, getStockSummaryReport, getInactiveAssetsReport, getMerchantActivationReport, getScrapLeakageTrendsReport, exportInventoryReport, sendLowStockAlert } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Users, Package, AlertTriangle, Download, RefreshCw, Store, Trash2, Mail } from 'lucide-react';
import { toast } from 'sonner';

const InventoryReports = ({ user, setUser }) => {
  const [tab, setTab] = useState('person-wise');
  const [loading, setLoading] = useState(false);

  // Person-wise
  const [personRows, setPersonRows] = useState([]);
  const [personTotals, setPersonTotals] = useState({});
  const [roleFilter, setRoleFilter] = useState('all');

  // Stock summary
  const [stockRows, setStockRows] = useState([]);
  const [stockSummary, setStockSummary] = useState({});
  const [threshold, setThreshold] = useState(10);

  // Inactive
  const [inactiveDays, setInactiveDays] = useState(30);
  const [deployedInactive, setDeployedInactive] = useState([]);
  const [stuckInTransit, setStuckInTransit] = useState([]);
  const [inactiveSummary, setInactiveSummary] = useState({});

  // Merchant activation
  const [activationRows, setActivationRows] = useState([]);
  const [activationSummary, setActivationSummary] = useState({});
  const [activationDays, setActivationDays] = useState(30);

  // Scrap / leakage trends
  const [trendMonths, setTrendMonths] = useState(6);
  const [trends, setTrends] = useState([]);
  const [trendTotals, setTrendTotals] = useState({});
  const [topContributors, setTopContributors] = useState([]);
  const [topHolders, setTopHolders] = useState([]);

  // Low-stock alert
  const [sendingAlert, setSendingAlert] = useState(false);

  const isAdmin = ['admin', 'hr_manager', 'warehouse_manager'].includes(user?.role);

  const loadPersonWise = async () => {
    setLoading(true);
    try {
      const r = await getPersonWiseReport(roleFilter === 'all' ? {} : { role: roleFilter });
      setPersonRows(r.data.rows); setPersonTotals(r.data.totals);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const loadStockSummary = async () => {
    setLoading(true);
    try {
      const r = await getStockSummaryReport({ low_threshold: threshold });
      setStockRows(r.data.rows); setStockSummary(r.data.summary);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const loadInactive = async () => {
    setLoading(true);
    try {
      const r = await getInactiveAssetsReport({ inactive_days: inactiveDays });
      setDeployedInactive(r.data.deployed_inactive);
      setStuckInTransit(r.data.stuck_in_transit);
      setInactiveSummary(r.data.summary);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const loadActivation = async () => {
    setLoading(true);
    try {
      const r = await getMerchantActivationReport({ inactive_days: activationDays });
      setActivationRows(r.data.rows); setActivationSummary(r.data.summary);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const loadTrends = async () => {
    setLoading(true);
    try {
      const r = await getScrapLeakageTrendsReport({ months: trendMonths });
      setTrends(r.data.trends); setTrendTotals(r.data.totals);
      setTopContributors(r.data.top_contributors); setTopHolders(r.data.top_holders);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const triggerLowStockAlert = async () => {
    if (!window.confirm(`Send low-stock alert email (threshold: ${threshold}) to all admins?`)) return;
    setSendingAlert(true);
    try {
      const r = await sendLowStockAlert({ threshold });
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send alert'); }
    finally { setSendingAlert(false); }
  };

  useEffect(() => {
    if (tab === 'person-wise') loadPersonWise();
    else if (tab === 'stock-summary') loadStockSummary();
    else if (tab === 'inactive') loadInactive();
    else if (tab === 'activation') loadActivation();
    else if (tab === 'trends') loadTrends();
    // eslint-disable-next-line
  }, [tab]);

  const exportReport = async () => {
    try {
      const params = { report_type: tab === 'inactive' ? 'inactive-assets' : tab };
      if (tab === 'person-wise' && roleFilter !== 'all') params.role = roleFilter;
      if (tab === 'stock-summary') params.low_threshold = threshold;
      if (tab === 'inactive') params.inactive_days = inactiveDays;
      const r = await exportInventoryReport(params);
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${params.report_type}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Exported');
    } catch { toast.error('Export failed'); }
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="inventory-reports">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Inventory Reports</h1>
            <p className="text-sm text-slate-500">Person-wise holdings, low-stock alerts, inactive assets</p>
          </div>
          <Button onClick={exportReport} variant="outline" data-testid="export-report-btn"><Download className="h-4 w-4 mr-1" />Export Excel</Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="person-wise" data-testid="tab-person-wise"><Users className="h-4 w-4 mr-1" />Person-wise</TabsTrigger>
            <TabsTrigger value="stock-summary" data-testid="tab-stock-summary"><Package className="h-4 w-4 mr-1" />Stock Summary</TabsTrigger>
            <TabsTrigger value="inactive" data-testid="tab-inactive"><AlertTriangle className="h-4 w-4 mr-1" />Inactive Assets</TabsTrigger>
            <TabsTrigger value="activation" data-testid="tab-activation"><Store className="h-4 w-4 mr-1" />Merchant Activation</TabsTrigger>
            <TabsTrigger value="trends" data-testid="tab-trends"><Trash2 className="h-4 w-4 mr-1" />Scrap & Leakage</TabsTrigger>
          </TabsList>

          {/* PERSON-WISE */}
          <TabsContent value="person-wise">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3 flex-wrap">
                  <div>
                    <Label className="text-xs">Role</Label>
                    <Select value={roleFilter} onValueChange={v => { setRoleFilter(v); }}>
                      <SelectTrigger className="w-48" data-testid="filter-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All field roles</SelectItem>
                        <SelectItem value="state_head">State Head</SelectItem>
                        <SelectItem value="asm">ASM</SelectItem>
                        <SelectItem value="tl">Team Lead</SelectItem>
                        <SelectItem value="fse">FSE</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" onClick={loadPersonWise} className="mt-5"><RefreshCw className="h-4 w-4 mr-1" />Apply</Button>
                  <div className="ml-auto mt-5 flex gap-3 text-sm">
                    <span className="text-slate-700"><b>{personTotals.people_with_stock || 0}</b>/{personTotals.total_people || 0} have stock</span>
                    <span className="text-indigo-700">Serialized: <b>{personTotals.total_serialized || 0}</b></span>
                    <span className="text-purple-700">Bulk: <b>{personTotals.total_bulk || 0}</b></span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {(personTotals.bulk_by_item_type || []).length > 0 && (
                  <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded flex flex-wrap gap-2 items-center" data-testid="bulk-by-item-type-ribbon">
                    <span className="text-xs font-medium text-purple-900">Bulk Stock in Field (by Item Type):</span>
                    {personTotals.bulk_by_item_type.map((b) => (
                      <span key={b.item_type_id} className="px-2 py-0.5 rounded bg-white border border-purple-300 text-xs text-purple-800">
                        {b.item_type_name}: <b>{(b.quantity_remaining || 0).toLocaleString()}</b>
                      </span>
                    ))}
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 text-xs font-medium">Name</th>
                        <th className="text-left p-2 text-xs font-medium">Role</th>
                        <th className="text-right p-2 text-xs font-medium">Serialized</th>
                        <th className="text-right p-2 text-xs font-medium">In Hand</th>
                        <th className="text-right p-2 text-xs font-medium">Deployed</th>
                        <th className="text-right p-2 text-xs font-medium">Bulk Units</th>
                        <th className="text-left p-2 text-xs font-medium">By Category</th>
                        <th className="text-left p-2 text-xs font-medium">Bulk by Item Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {personRows.map(r => (
                        <tr key={r.user_id} className="border-b hover:bg-slate-50">
                          <td className="p-2"><p className="font-medium">{r.name}</p><p className="text-xs text-slate-500">{r.email}</p></td>
                          <td className="p-2"><span className="px-2 py-0.5 text-xs rounded bg-indigo-100 text-indigo-700 uppercase">{r.role}</span></td>
                          <td className="p-2 text-right font-semibold">{r.serialized_count}</td>
                          <td className="p-2 text-right text-blue-700">{r.in_hand_count}</td>
                          <td className="p-2 text-right text-green-700">{r.deployed_count}</td>
                          <td className="p-2 text-right text-purple-700 font-semibold">{r.bulk_total}</td>
                          <td className="p-2 text-xs">
                            {Object.entries(r.by_category || {}).map(([k, v]) => (
                              <span key={k} className="inline-block mr-1 px-1.5 py-0.5 bg-slate-100 rounded">
                                {k}: <b>{v}</b>
                              </span>
                            ))}
                          </td>
                          <td className="p-2 text-xs">
                            {(r.bulk_by_item_type || []).map((b) => (
                              <span key={b.item_type_id} className="inline-block mr-1 mb-1 px-1.5 py-0.5 bg-purple-50 border border-purple-200 rounded text-purple-800">
                                {b.item_type_name}: <b>{(b.quantity_remaining || 0).toLocaleString()}</b>
                              </span>
                            ))}
                            {(r.bulk_by_item_type || []).length === 0 && <span className="text-slate-400">-</span>}
                          </td>
                        </tr>
                      ))}
                      {personRows.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-slate-500">{loading ? 'Loading...' : 'No data'}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* STOCK SUMMARY */}
          <TabsContent value="stock-summary">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3 flex-wrap">
                  <div>
                    <Label className="text-xs">Low Stock Threshold</Label>
                    <Input type="number" min="1" value={threshold} onChange={e => setThreshold(parseInt(e.target.value) || 0)} className="w-32" data-testid="threshold-input" />
                  </div>
                  <Button variant="outline" onClick={loadStockSummary} className="mt-5"><RefreshCw className="h-4 w-4 mr-1" />Apply</Button>
                  {isAdmin && (
                    <Button onClick={triggerLowStockAlert} disabled={sendingAlert} className="mt-5 bg-amber-600 hover:bg-amber-700" data-testid="send-low-stock-alert">
                      <Mail className="h-4 w-4 mr-1" />{sendingAlert ? 'Sending...' : 'Send Low-Stock Alert'}
                    </Button>
                  )}
                  <div className="ml-auto mt-5 flex gap-3 text-sm">
                    <span className="text-slate-700">Item Types: <b>{stockSummary.total_item_types || 0}</b></span>
                    <span className="text-amber-700">Low Stock: <b>{stockSummary.low_stock_count || 0}</b></span>
                    <span className="text-red-700">Out of Stock: <b>{stockSummary.out_of_stock_count || 0}</b></span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 text-xs font-medium">Item Type</th>
                        <th className="text-left p-2 text-xs font-medium">Category</th>
                        <th className="text-left p-2 text-xs font-medium">Tracking</th>
                        <th className="text-right p-2 text-xs font-medium">Available</th>
                        <th className="text-right p-2 text-xs font-medium">Assigned</th>
                        <th className="text-right p-2 text-xs font-medium">Deployed</th>
                        <th className="text-right p-2 text-xs font-medium">Inactive</th>
                        <th className="text-right p-2 text-xs font-medium">Scrap/Lost</th>
                        <th className="text-center p-2 text-xs font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockRows.map(r => (
                        <tr key={r.item_type_id} className={`border-b ${r.is_low_stock ? 'bg-amber-50' : ''} hover:bg-slate-50`}>
                          <td className="p-2 font-medium">{r.name}</td>
                          <td className="p-2 capitalize text-xs">{(r.category || '').replace('_', ' ')}</td>
                          <td className="p-2"><span className={`px-2 py-0.5 text-xs rounded ${r.tracking_mode === 'serialized' ? 'bg-indigo-100 text-indigo-700' : 'bg-purple-100 text-purple-700'}`}>{r.tracking_mode}</span></td>
                          <td className={`p-2 text-right font-bold ${r.is_low_stock ? 'text-red-700' : 'text-slate-900'}`}>{r.available}</td>
                          <td className="p-2 text-right">{r.assigned}</td>
                          <td className="p-2 text-right text-green-700">{r.deployed}</td>
                          <td className="p-2 text-right text-amber-700">{r.inactive}</td>
                          <td className="p-2 text-right text-red-700">{r.scrapped + r.lost}</td>
                          <td className="p-2 text-center">
                            {r.available === 0 ? <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-700">Out of Stock</span> :
                             r.is_low_stock ? <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-700">Low</span> :
                             <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700">OK</span>}
                          </td>
                        </tr>
                      ))}
                      {stockRows.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-slate-500">{loading ? 'Loading...' : 'No data'}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* INACTIVE ASSETS */}
          <TabsContent value="inactive">
            <Card className="mb-4">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 flex-wrap">
                  <div>
                    <Label className="text-xs">Inactive Days Threshold</Label>
                    <Input type="number" min="1" value={inactiveDays} onChange={e => setInactiveDays(parseInt(e.target.value) || 0)} className="w-32" data-testid="inactive-days" />
                  </div>
                  <Button variant="outline" onClick={loadInactive} className="mt-5"><RefreshCw className="h-4 w-4 mr-1" />Apply</Button>
                  <div className="ml-auto mt-5 flex gap-3 text-sm">
                    <span className="text-orange-700">Inactive Deployments: <b>{inactiveSummary.deployed_inactive_count || 0}</b></span>
                    <span className="text-amber-700">Stuck in Transit: <b>{inactiveSummary.stuck_in_transit_count || 0}</b></span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Inactive Deployed */}
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-orange-600" />Inactive Deployments ({deployedInactive.length})</CardTitle><p className="text-xs text-slate-500">Sound Boxes / POS deployed to merchants but not transacting in last {inactiveDays} days</p></CardHeader>
                <CardContent>
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr><th className="text-left p-2">Serial #</th><th className="text-left p-2">Item</th><th className="text-left p-2">Held By</th><th className="text-left p-2">Activated</th></tr>
                      </thead>
                      <tbody>
                        {deployedInactive.map(i => (
                          <tr key={i.id} className="border-b">
                            <td className="p-2 font-mono text-indigo-700">{i.serial_number}</td>
                            <td className="p-2">{i.item_type_name}</td>
                            <td className="p-2">{i.holder_name}</td>
                            <td className="p-2 text-slate-500">{i.activated_at ? new Date(i.activated_at).toLocaleDateString() : '-'}</td>
                          </tr>
                        ))}
                        {deployedInactive.length === 0 && <tr><td colSpan="4" className="p-6 text-center text-slate-500">✓ No inactive deployments</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Stuck in Transit */}
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" />Stuck in Transit ({stuckInTransit.length})</CardTitle><p className="text-xs text-slate-500">Items handed to field staff but not deployed to a merchant in {inactiveDays}+ days</p></CardHeader>
                <CardContent>
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr><th className="text-left p-2">Serial #</th><th className="text-left p-2">Item</th><th className="text-left p-2">Held By</th><th className="text-left p-2">Last Update</th></tr>
                      </thead>
                      <tbody>
                        {stuckInTransit.map(i => (
                          <tr key={i.id} className="border-b">
                            <td className="p-2 font-mono text-indigo-700">{i.serial_number}</td>
                            <td className="p-2">{i.item_type_name}</td>
                            <td className="p-2">{i.holder_name}</td>
                            <td className="p-2 text-slate-500">{i.updated_at ? new Date(i.updated_at).toLocaleDateString() : '-'}</td>
                          </tr>
                        ))}
                        {stuckInTransit.length === 0 && <tr><td colSpan="4" className="p-6 text-center text-slate-500">✓ Nothing stuck</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* MERCHANT ACTIVATION */}
          <TabsContent value="activation">
            <Card className="mb-4">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 flex-wrap">
                  <div>
                    <Label className="text-xs">Activity Window (days)</Label>
                    <Input type="number" min="1" value={activationDays} onChange={e => setActivationDays(parseInt(e.target.value) || 30)} className="w-32" data-testid="activation-days" />
                  </div>
                  <Button variant="outline" onClick={loadActivation} className="mt-5"><RefreshCw className="h-4 w-4 mr-1" />Apply</Button>
                  <div className="ml-auto mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><span className="text-slate-500 text-xs">Merchants w/ assets</span><p className="font-bold">{activationSummary.merchants_with_assets || 0}</p></div>
                    <div><span className="text-slate-500 text-xs">Fully Active</span><p className="font-bold text-green-700">{activationSummary.fully_active_merchants || 0}</p></div>
                    <div><span className="text-slate-500 text-xs">Zero Active</span><p className="font-bold text-red-700">{activationSummary.zero_active_merchants || 0}</p></div>
                    <div><span className="text-slate-500 text-xs">Overall Rate</span><p className="font-bold text-indigo-700">{activationSummary.overall_activation_rate || 0}%</p></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 text-xs font-medium">Merchant</th>
                        <th className="text-left p-2 text-xs font-medium">Location</th>
                        <th className="text-right p-2 text-xs font-medium">Issued</th>
                        <th className="text-right p-2 text-xs font-medium">Deployed</th>
                        <th className="text-right p-2 text-xs font-medium">Active</th>
                        <th className="text-right p-2 text-xs font-medium">Inactive</th>
                        <th className="text-right p-2 text-xs font-medium">Activation %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activationRows.map(r => (
                        <tr key={r.merchant_id} className={`border-b ${r.activation_rate === 0 && r.deployed_count > 0 ? 'bg-red-50' : ''} hover:bg-slate-50`}>
                          <td className="p-2">
                            <p className="font-medium">{r.merchant_name}</p>
                            <p className="text-xs text-slate-500 font-mono">{r.merchant_code}</p>
                          </td>
                          <td className="p-2 text-xs">{[r.city, r.state].filter(Boolean).join(', ')}</td>
                          <td className="p-2 text-right">{r.total_assets_issued}</td>
                          <td className="p-2 text-right">{r.deployed_count}</td>
                          <td className="p-2 text-right text-green-700 font-medium">{r.active_count}</td>
                          <td className="p-2 text-right text-amber-700">{r.inactive_count}</td>
                          <td className="p-2 text-right">
                            <span className={`px-2 py-0.5 text-xs rounded font-bold ${
                              r.activation_rate === 100 ? 'bg-green-100 text-green-700' :
                              r.activation_rate >= 50 ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>{r.activation_rate}%</span>
                          </td>
                        </tr>
                      ))}
                      {activationRows.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-slate-500">{loading ? 'Loading...' : 'No merchants yet'}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SCRAP & LEAKAGE TRENDS */}
          <TabsContent value="trends">
            <Card className="mb-4">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 flex-wrap">
                  <div>
                    <Label className="text-xs">Trailing Months</Label>
                    <Input type="number" min="1" max="24" value={trendMonths} onChange={e => setTrendMonths(parseInt(e.target.value) || 6)} className="w-32" data-testid="trend-months" />
                  </div>
                  <Button variant="outline" onClick={loadTrends} className="mt-5"><RefreshCw className="h-4 w-4 mr-1" />Apply</Button>
                  <div className="ml-auto mt-5 grid grid-cols-3 gap-3 text-sm">
                    <div><span className="text-slate-500 text-xs">Total Scrapped</span><p className="font-bold text-red-700">{trendTotals.total_scrapped || 0}</p></div>
                    <div><span className="text-slate-500 text-xs">Total Lost</span><p className="font-bold text-red-700">{trendTotals.total_lost || 0}</p></div>
                    <div><span className="text-slate-500 text-xs">Total Returned</span><p className="font-bold text-amber-700">{trendTotals.total_returned || 0}</p></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Monthly trend */}
              <Card>
                <CardHeader><CardTitle className="text-base">Monthly Trend</CardTitle></CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 text-xs font-medium">Month</th>
                        <th className="text-right p-2 text-xs font-medium">Scrap</th>
                        <th className="text-right p-2 text-xs font-medium">Lost</th>
                        <th className="text-right p-2 text-xs font-medium">Returns</th>
                        <th className="text-right p-2 text-xs font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trends.map(t => (
                        <tr key={t.month} className="border-b">
                          <td className="p-2 font-medium">{t.month}</td>
                          <td className="p-2 text-right text-red-700">{t.scrap}</td>
                          <td className="p-2 text-right text-red-700">{t.lost}</td>
                          <td className="p-2 text-right text-amber-700">{t.returned}</td>
                          <td className="p-2 text-right font-bold">{t.total}</td>
                        </tr>
                      ))}
                      {trends.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-slate-500">No scrap/leakage activity</td></tr>}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {/* Top contributors */}
              <div className="space-y-4">
                <Card>
                  <CardHeader><CardTitle className="text-base">Top Item Types (Scrap + Lost)</CardTitle></CardHeader>
                  <CardContent>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-2 text-xs font-medium">Item Type</th>
                          <th className="text-right p-2 text-xs font-medium">Scrapped</th>
                          <th className="text-right p-2 text-xs font-medium">Lost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topContributors.map((c, i) => (
                          <tr key={i} className="border-b">
                            <td className="p-2 font-medium">{c.item_type_name}</td>
                            <td className="p-2 text-right text-red-700">{c.scrapped || 0}</td>
                            <td className="p-2 text-right text-red-700">{c.lost || 0}</td>
                          </tr>
                        ))}
                        {topContributors.length === 0 && <tr><td colSpan="3" className="p-6 text-center text-slate-500">No data</td></tr>}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="text-base">Top Holders (Scrap/Lost source)</CardTitle></CardHeader>
                  <CardContent>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-2 text-xs font-medium">Holder</th>
                          <th className="text-right p-2 text-xs font-medium">Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topHolders.map((h, i) => (
                          <tr key={i} className="border-b">
                            <td className="p-2 font-medium">{h.holder_name}</td>
                            <td className="p-2 text-right font-bold text-red-700">{h.count}</td>
                          </tr>
                        ))}
                        {topHolders.length === 0 && <tr><td colSpan="2" className="p-6 text-center text-slate-500">No data</td></tr>}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default InventoryReports;
